<template>
  <v-dialog v-model="localShow" max-width="600">
    <v-card>
      <v-card-title :class="isEditing ? 'bg-primary' : 'bg-success'" class="text-white">
        <v-icon class="mr-2">{{ isEditing ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
        {{ isEditing ? 'Edit Operation' : 'Add Operation' }}
      </v-card-title>
      <v-card-text class="pt-4">
        <v-form ref="form">
          <!-- Service Select -->
          <v-autocomplete
            v-model="localForm.service_id"
            :items="services"
            item-value="id"
            item-title="name"
            label="Service *"
            :rules="[v => !!v || 'Service is required']"
            outlined
            dense
            clearable
          ></v-autocomplete>

          <!-- Operation Type (from DB, by ID) -->
          <v-select
            v-model="localForm.service_operation_type_id"
            :items="operationTypeOptions"
            item-value="value"
            item-title="label"
            label="Operation Type *"
            :rules="[v => !!v || 'Operation type is required']"
            outlined
            dense
          >
            <template #item="{ item, props }">
              <v-list-item v-bind="props">
                <template #prepend>
                  <v-chip :color="item.raw.color || 'grey'" size="x-small" class="mr-2">&nbsp;</v-chip>
                </template>
              </v-list-item>
            </template>
          </v-select>

          <!-- MVNO API Method (from DB, by ID) -->
          <v-select
            v-model="localForm.mvno_api_method_id"
            :items="mvnoApiMethodOptions"
            item-value="value"
            item-title="label"
            label="MVNO API Method *"
            :rules="[v => !!v || 'MVNO API method is required']"
            outlined
            dense
          ></v-select>

          <!-- Description -->
          <v-text-field
            v-model="localForm.description"
            label="Description"
            outlined
            dense
          ></v-text-field>

          <v-divider class="my-3"></v-divider>
          <div class="text-subtitle-2 mb-2">API Parameters</div>

          <!-- Credit Source -->
          <v-row>
            <v-col cols="6">
              <v-select
                v-model="apiParamsForm.credit_source"
                :items="creditSourceOptions"
                item-value="value"
                item-title="label"
                label="Credit Source"
                outlined
                dense
                hint="Where to get the credit value from"
                persistent-hint
              ></v-select>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-if="apiParamsForm.credit_source === 'fixed'"
                v-model.number="apiParamsForm.credit_value"
                label="Credit Value"
                type="number"
                outlined
                dense
                hint="Fixed credit amount"
                persistent-hint
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Valability Source -->
          <v-row>
            <v-col cols="6">
              <v-select
                v-model="apiParamsForm.valability_source"
                :items="valabilitySourceOptions"
                item-value="value"
                item-title="label"
                label="Valability Source"
                outlined
                dense
                hint="Where to get the validity days from"
                persistent-hint
              ></v-select>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-if="apiParamsForm.valability_source === 'fixed'"
                v-model.number="apiParamsForm.valability_value"
                label="Valability Value (days)"
                type="number"
                outlined
                dense
                hint="Fixed validity in days"
                persistent-hint
              ></v-text-field>
            </v-col>
          </v-row>

          <v-divider class="my-3"></v-divider>

          <!-- Active switch -->
          <v-switch
            v-model="localForm.active"
            label="Active"
            :color="localForm.active ? 'success' : 'error'"
            hide-details
          ></v-switch>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="close">Cancel</v-btn>
        <v-btn color="primary" @click="save" :loading="saving">
          {{ isEditing ? 'Update' : 'Add' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'ServiceMvnoOperationDialog',
  props: {
    show: { type: Boolean, default: false },
    isEditing: { type: Boolean, default: false },
    formData: { type: Object, default: () => ({}) },
    services: { type: Array, default: () => [] },
    operationTypes: { type: Array, default: () => [] },
    mvnoApiMethods: { type: Array, default: () => [] },
    saving: { type: Boolean, default: false }
  },
  emits: ['close', 'save'],
  data() {
    return {
      localForm: {
        id: null,
        service_id: null,
        service_operation_type_id: null,
        mvno_api_method_id: null,
        description: '',
        active: true
      },
      apiParamsForm: {
        credit_source: 'service_price',
        credit_value: null,
        valability_source: 'service_validity',
        valability_value: null
      },
      creditSourceOptions: [
        { value: 'service_price', label: 'Service Price (default)' },
        { value: 'fixed', label: 'Fixed Value' },
        { value: 'none', label: 'Skip (don\'t send)' }
      ],
      valabilitySourceOptions: [
        { value: 'service_validity', label: 'Service Validity (default)' },
        { value: 'fixed', label: 'Fixed Value' },
        { value: 'none', label: 'Skip (don\'t send)' }
      ]
    }
  },
  computed: {
    localShow: {
      get() { return this.show },
      set(val) { if (!val) this.$emit('close') }
    },
    operationTypeOptions() {
      return this.operationTypes.map(ot => ({
        value: ot.id,
        label: ot.label || ot.name,
        color: ot.color || 'grey'
      }))
    },
    mvnoApiMethodOptions() {
      return this.mvnoApiMethods.map(m => ({
        value: m.id,
        label: m.description || m.name.replace(/_/g, ' ')
      }))
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.localForm = {
          id: this.formData.id || null,
          service_id: this.formData.service_id || null,
          service_operation_type_id: this.formData.service_operation_type_id || null,
          mvno_api_method_id: this.formData.mvno_api_method_id || null,
          description: this.formData.description || '',
          active: this.formData.active !== undefined ? this.formData.active : true
        }
        const params = this.formData.api_params || {}
        this.apiParamsForm = {
          credit_source: params.credit_source || 'service_price',
          credit_value: params.credit_value || null,
          valability_source: params.valability_source || 'service_validity',
          valability_value: params.valability_value || null
        }
      }
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
    buildApiParams() {
      const params = {}
      if (this.apiParamsForm.credit_source && this.apiParamsForm.credit_source !== 'service_price') {
        params.credit_source = this.apiParamsForm.credit_source
        if (this.apiParamsForm.credit_source === 'fixed' && this.apiParamsForm.credit_value != null) {
          params.credit_value = this.apiParamsForm.credit_value
        }
      }
      if (this.apiParamsForm.valability_source && this.apiParamsForm.valability_source !== 'service_validity') {
        params.valability_source = this.apiParamsForm.valability_source
        if (this.apiParamsForm.valability_source === 'fixed' && this.apiParamsForm.valability_value != null) {
          params.valability_value = this.apiParamsForm.valability_value
        }
      }
      return Object.keys(params).length > 0 ? params : null
    },
    save() {
      if (!this.localForm.service_id || !this.localForm.service_operation_type_id || !this.localForm.mvno_api_method_id) {
        return
      }
      const apiParams = this.buildApiParams()
      this.$emit('save', {
        ...this.localForm,
        api_params: apiParams
      })
    }
  }
}
</script>
